import React from 'react';
import { IconButton, MinusIcon, Pane, PlusIcon } from 'evergreen-ui';
import {
  MapContainer as LeafletMap, useMap, GeoJSON,
} from 'react-leaflet';
import L from 'leaflet';
import MapLegend from './Map.Legend';
import MapMarkerCluster from './MarkerCluster';
import useMapShowGeoJSON from './useMapShowGeoJSON';
import data from './admin_level.json';

type HomeMapProps = {
  whenMapCreated: (map: L.Map) => void
}

export default function Map({ whenMapCreated }: HomeMapProps) {
  const mapContainerRef = React.useRef(null);
  const mapRef = React.useRef<L.Map | null>(null);
  const { isShowGeoJSON } = useMapShowGeoJSON();

  const mapBounds = React.useMemo(() => {
    return L.latLngBounds(
      L.latLng(-100, -100),
      L.latLng(100, 300),
    );
  }, []);

  // Создаем наблюдателя для слежения за размером контейнера карты
  const observer = React.useMemo(() => {
    return new ResizeObserver(() => {
      /* 
        при скрытии сайдбара изменяется размер контейнера 
        и пересчитываем размер карты (иначе будут серые полосы по бокам) 
      */
      mapRef.current?.invalidateSize();
    });
  }, []);

  const onCreated = React.useCallback((map: L.Map) => {
    if (whenMapCreated) {
      whenMapCreated(map);
    }
    mapRef.current = map;
  }, [whenMapCreated]);

  React.useEffect(() => {
    const target = mapContainerRef.current;
    if (target) {
      observer.observe(target);
    }
    return () => {
      observer.disconnect();
    };
  }, [observer]);

  return (
    <Pane
      width="100%"
      height="100%"
      position="relative"
      ref={mapContainerRef}
    >
      <LeafletMap
        zoomControl={false}
        pan={{ animate: false, duration: 0 }}
        center={[56.69451540694317, 50.53951263427735]}
        zoom={4}
        maxZoom={17}
        minZoom={4}
        animate={false}
        zoomAnimation={false}
        markerZoomAnimation={false}
        maxBoundsViscosity={1.0}
        maxBounds={mapBounds}
        attributionControl={false}
        doubleClickZoom={false}
        whenCreated={onCreated}
      >
        {isShowGeoJSON && <GeoJSON key="my-geojson" data={data as any} />}
        <MapZoomControl />
        <MapMarkerCluster />
        <MapLegend />
      </LeafletMap>
    </Pane>
  );
}

function MapZoomControl() {
  const map = useMap();

  const zoomIn = () => {
    map.zoomIn();
  };

  const zoomOut = () => {
    map.zoomOut();
  };

  return (
    <Pane
      position="absolute"
      zIndex={401}
      backgroundColor="white"
      boxShadow="0px 0px 20px 0px rgba(0, 0, 0, .25)"
      top={10}
      right={10}
    >
      <IconButton
        icon={PlusIcon}
        appearance="minimal"
        onClick={zoomIn}
      />
      <IconButton
        icon={MinusIcon}
        appearance="minimal"
        onClick={zoomOut}
      />
    </Pane>
  );
}
